import { useControlledState } from '@react-stately/utils';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { translationLanguageDescriptors } from '@gitbook/app-translations';
import { useMenuTriggerState } from '@gitbook/spine-react';

import type { BaseLocalizedInputProps, LocalizedLanguagesList } from './types';

/**
 * Manages the state for a localized input component.
 */
export function useLocalizedInputState(
    options: Pick<
        BaseLocalizedInputProps,
        'selectedLanguage' | 'onSelectedLanguageChange' | 'sortLanguages'
    >
) {
    const {
        selectedLanguage: controlledLanguage,
        onSelectedLanguageChange,
        sortLanguages,
    } = options;
    const [selectedLanguage, setSelectedLanguage] =
        useControlledState<api.TranslationLanguage | null>(
            controlledLanguage,
            null,
            onSelectedLanguageChange
        );
    const inputRef = React.useRef<HTMLInputElement>(null);
    const menuTrigger = useMenuTriggerState();

    const selectLanguage = (language: api.TranslationLanguage | null) => {
        setSelectedLanguage(language);
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    const languages: LocalizedLanguagesList = sortLanguages
        ? sortLanguages(translationLanguageDescriptors)
        : translationLanguageDescriptors;

    return {
        selectedLanguage,
        setSelectedLanguage: selectLanguage,
        inputRef,
        menuTrigger,
        languages,
    };
}
